import { ComponentChildren } from 'preact';

export interface ComponentShowcase {
  name: string;
  category: string;
  description: string;
  usedIn: string[];
  example: () => ComponentChildren;
}

// ⏳ Loading
import { showcase as SkeletonBarShowcase } from './showcases/SkeletonBar.showcase';
import { showcase as PageShellShowcase } from './showcases/PageShell.showcase';
import { showcase as PageLoadingShowcase } from './showcases/PageLoading.showcase';
import { showcase as CardSkeletonShowcase } from './showcases/CardSkeleton.showcase';
import { showcase as ListSkeletonShowcase } from './showcases/ListSkeleton.showcase';
import { showcase as TableSkeletonShowcase } from './showcases/TableSkeleton.showcase';
import { showcase as SpinnerShowcase } from './showcases/Spinner.showcase';

// 🔘 Botões
import { showcase as ButtonShowcase } from './showcases/Button.showcase';
import { showcase as CopyButtonShowcase } from './showcases/CopyButton.showcase';
import { showcase as ShareButtonShowcase } from './showcases/ShareButton.showcase';
import { showcase as IconButtonShowcase } from './showcases/IconButton.showcase';

// 📋 Cards
import { showcase as CardShowcase } from './showcases/Card.showcase';
import { showcase as MetricCardShowcase } from './showcases/MetricCard.showcase';

// 📝 Formulários
import { showcase as InputShowcase } from './showcases/Input.showcase';
import { showcase as SelectShowcase } from './showcases/Select.showcase';
import { showcase as TextareaShowcase } from './showcases/Textarea.showcase';
import { showcase as ToggleShowcase } from './showcases/Toggle.showcase';
import { showcase as FormGroupShowcase } from './showcases/FormGroup.showcase';
import { showcase as SearchInputShowcase } from './showcases/SearchInput.showcase';

// 📊 Data Display
import { showcase as BadgeShowcase } from './showcases/Badge.showcase';
import { showcase as StatusBadgeShowcase } from './showcases/StatusBadge.showcase';
import { showcase as AvatarShowcase } from './showcases/Avatar.showcase';
import { showcase as ProgressBarShowcase } from './showcases/ProgressBar.showcase';
import { showcase as TableShowcase } from './showcases/Table.showcase';
import { showcase as PaginationShowcase } from './showcases/Pagination.showcase';
import { showcase as FilterChipsShowcase } from './showcases/FilterChips.showcase';

// 🗂️ Navegação
import { showcase as TabBarShowcase } from './showcases/TabBar.showcase';
import { showcase as DropdownShowcase } from './showcases/Dropdown.showcase';
import { showcase as AccordionShowcase } from './showcases/Accordion.showcase';
import { showcase as SectionHeaderShowcase } from './showcases/SectionHeader.showcase';

// 💬 Chat
import { showcase as ChatBubbleShowcase } from './showcases/ChatBubble.showcase';
import { showcase as TypingIndicatorShowcase } from './showcases/TypingIndicator.showcase';
import { showcase as OnlineDotShowcase } from './showcases/OnlineDot.showcase';

// 🪟 Modais & Overlay
import { showcase as ModalShowcase } from './showcases/Modal.showcase';
import { showcase as ConfirmDialogShowcase } from './showcases/ConfirmDialog.showcase';
import { showcase as ToastShowcase } from './showcases/Toast.showcase';
import { showcase as AlertShowcase } from './showcases/Alert.showcase';
import { showcase as TooltipShowcase } from './showcases/Tooltip.showcase';

// 📐 Layout
import { showcase as DividerShowcase } from './showcases/Divider.showcase';
import { showcase as EmptyStateShowcase } from './showcases/EmptyState.showcase';

// 🏷️ Badges & Indicadores
import { showcase as ChannelBadgeShowcase } from './showcases/ChannelBadge.showcase';
import { showcase as SlotsRemainingBadgeShowcase } from './showcases/SlotsRemainingBadge.showcase';
import { showcase as CountdownTimerShowcase } from './showcases/CountdownTimer.showcase';
import { showcase as BalanceIndicatorShowcase } from './showcases/BalanceIndicator.showcase';
import { showcase as TokenCounterShowcase } from './showcases/TokenCounter.showcase';
import { showcase as StreamingCursorShowcase } from './showcases/StreamingCursor.showcase';

// 📋 Cards & Layout
import { showcase as SectionDividerShowcase } from './showcases/SectionDivider.showcase';
import { showcase as LevelProgressBarShowcase } from './showcases/LevelProgressBar.showcase';

// 💬 Chat (FASE 5)
import { showcase as ModelSelectorShowcase } from './showcases/ModelSelector.showcase';
import { showcase as AttachMenuShowcase } from './showcases/AttachMenu.showcase';
import { showcase as ChatHeaderShowcase } from './showcases/ChatHeader.showcase';
import { showcase as PromoBannerShowcase } from './showcases/PromoBanner.showcase';
import { showcase as RechargeModalShowcase } from './showcases/RechargeModal.showcase';

export const componentRegistry: ComponentShowcase[] = [
  // ⏳ Loading
  SkeletonBarShowcase,
  PageShellShowcase,
  PageLoadingShowcase,
  CardSkeletonShowcase,
  ListSkeletonShowcase,
  TableSkeletonShowcase,
  SpinnerShowcase,
  // 🔘 Botões
  ButtonShowcase,
  CopyButtonShowcase,
  ShareButtonShowcase,
  IconButtonShowcase,
  // 📋 Cards
  CardShowcase,
  MetricCardShowcase,
  // 📝 Formulários
  InputShowcase,
  SelectShowcase,
  TextareaShowcase,
  ToggleShowcase,
  FormGroupShowcase,
  SearchInputShowcase,
  // 📊 Data Display
  BadgeShowcase,
  StatusBadgeShowcase,
  AvatarShowcase,
  ProgressBarShowcase,
  TableShowcase,
  PaginationShowcase,
  FilterChipsShowcase,
  // 🗂️ Navegação
  TabBarShowcase,
  DropdownShowcase,
  AccordionShowcase,
  SectionHeaderShowcase,
  // 💬 Chat
  ChatBubbleShowcase,
  TypingIndicatorShowcase,
  OnlineDotShowcase,
  // 🪟 Modais & Overlay
  ModalShowcase,
  ConfirmDialogShowcase,
  ToastShowcase,
  AlertShowcase,
  TooltipShowcase,
  // 📐 Layout
  DividerShowcase,
  EmptyStateShowcase,
  // 🏷️ Badges & Indicadores
  ChannelBadgeShowcase,
  SlotsRemainingBadgeShowcase,
  CountdownTimerShowcase,
  BalanceIndicatorShowcase,
  TokenCounterShowcase,
  StreamingCursorShowcase,
  // 📋 Cards & Layout
  SectionDividerShowcase,
  LevelProgressBarShowcase,
  // 💬 Chat (FASE 5)
  ModelSelectorShowcase,
  AttachMenuShowcase,
  ChatHeaderShowcase,
  PromoBannerShowcase,
  RechargeModalShowcase,
];

// Helper pra agrupar por categoria
export function getCategories(registry: ComponentShowcase[]): string[] {
  return [...new Set(registry.map(c => c.category))];
}
